import type { OrganizationSeatUsageResponse } from 'generatedTypes/user/models';
import { useCallback } from 'react';

import useFetch from 'hooks/useFetch';
import useGlobalStore from 'hooks/useGlobalStore';
import useToast from 'hooks/useToast';

import useTranslation from './hooks/useTranslation';

import { ORGANIZATIONS_API, ORGANIZATION_SEATS_API } from './constants';
import type { FetchOrganizations, OrganizationRequest, Organizations } from './types';

const useOrganizations = () => {
    const { getValue, setValue } = useGlobalStore({
        namespace: 'useOrganizations'
    });

    const { FETCHING_SEATS_ERROR } = useTranslation();

    const { broadcastToast } = useToast();

    const { authenticatedGet, authenticatedPost } = useFetch();

    const createOrganization = useCallback(
        async (values: OrganizationRequest) => {
            const { companyName, location, phone } = values;
            const { address1, address2 = '', city, state, zipCode } = location;

            const requestData = {
                address1,
                address2: address2 ?? '',
                city,
                country: 'US',
                name: companyName,
                phoneCountryCode: '1',
                phoneNumber: phone?.replaceAll(/[-() ]/g, ''),
                state,
                zipCode
            };

            const response = await authenticatedPost(
                ORGANIZATIONS_API,
                JSON.stringify(requestData)
            );

            return response;
        },
        [authenticatedPost]
    );

    const fetchOrganizations: FetchOrganizations = useCallback(async () => {
        setValue({ isLoading: true });

        const output = await authenticatedGet(ORGANIZATIONS_API);

        setValue({ isLoading: false });

        const organizations = (output?.data?.content ?? []) as Organizations;

        setValue({ organizations });

        return { data: organizations, error: output.error };
    }, [authenticatedGet, setValue]);

    const fetchAvailableSeats = useCallback(async (): Promise<OrganizationSeatUsageResponse> => {
        try {
            const { data } = await authenticatedGet(ORGANIZATION_SEATS_API);

            return data;
        } catch (error) {
            broadcastToast({ text: FETCHING_SEATS_ERROR, type: 'error' });

            console.error('Failed to fetch organization seats:', error);
            throw error;
        }
    }, [FETCHING_SEATS_ERROR, authenticatedGet, broadcastToast]);

    return {
        createOrganization,
        fetchAvailableSeats,
        fetchOrganizations,
        isLoading: getValue('isLoading'),
        organizations: getValue('organizations')
    };
};

export default useOrganizations;
